import React from 'react';
import BackButton from './BackButton';

/**
 * PageTitle — the page's title, set large in the heading serif directly on the
 * page background (visual refresh, 2026-10-01). Replaces the gradient title
 * box and the solid-blue task header: no box, no fill, no shadow.
 *
 * Keep the title to one line at 390px. Don't add a subtitle that explains what
 * the page is; `detail` is for a fact (a date, a count), not a description.
 *
 * Props:
 * @param {React.ReactNode} title
 * @param {React.ReactNode} detail - Optional one-line fact under the title
 * @param {React.ReactNode} actions - Optional right-side slot (a Button, an
 *   IconButton, a weather chip). Keep it small; it shares the title's row.
 * @param {boolean|string} back - true: a back button that goes back in
 *   history. A string: the same, but when there is no in-app history (the
 *   page was opened directly from a notification, a shared link or a fresh
 *   tab) it goes to that route instead of leaving the app.
 * @param {string} className - Extra classes on the <header>
 */
function PageTitle({ title, detail = null, actions = null, back = false, className = '' }) {
  return (
    <header className={`flex items-start gap-1 pt-2 pb-4 ${className}`}>
      {back && <BackButton fallback={typeof back === 'string' ? back : null} className="-ml-2.5 mt-0.5" />}
      <div className="min-w-0 flex-1">
        {/* break-words: titles are often user-entered (collection, board
            names); one long word or a pasted URL must wrap, not push the
            page sideways. */}
        <h1 className="font-display font-medium text-3xl sm:text-4xl leading-tight text-content-primary [text-wrap:balance] break-words">
          {title}
        </h1>
        {detail && <p className="mt-1 text-sm text-content-secondary">{detail}</p>}
      </div>
      {actions && <div className="flex-shrink-0 flex items-center gap-2 self-center">{actions}</div>}
    </header>
  );
}

export default PageTitle;
